import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Chip, Box } from '@mui/material'
import { parse, stringify } from 'qs'
import DoneIcon from '@mui/icons-material/Done'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'

import { TableUrlPagination } from '../../../../ui'
import { TableActions } from '../table-actions'
import { useJobsContext } from '../../contexts'
import { JOBS_TABLE_COLUMNS } from './report-table.constants'
import { DEFAULT_QUERY } from '../../../campaigns/components/table/campaigns-table.constants'

export const ReportTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const {
    data,
    loading,
    search,
    getBatchsReportTable,
    setSelectedTableRows,
    getBatchsReportTableById,
    onClickReportTableRow,
    pagination: { total_reports: totalEntries },
  } = useJobsContext()

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const batchId = urlSearchParams.batch_id
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }

      if (batchId) {
        getBatchsReportTableById(batchId, searchParams)
      } else {
        getBatchsReportTable(searchParams)
      }
    } else {
      navigate(`/jobs/historic?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getBatchsReportTable, getBatchsReportTableById, navigate])

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      JOBS_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.jobs.report.table.columns.${column.field}.title` }),
          size: column.width,
        }

        if (column.field === 'actions') {
          return {
            ...baseColumn,
            Cell: (props) => <TableActions {...props} />,
            muiTableBodyCellProps: { align: 'center' },
          }
        }
        if (column.field === 'active') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => <input type="checkbox" checked={cell.getValue()} disabled />,
            muiTableBodyCellProps: { align: 'center' },
          }
        }
        if (column.field === 'status') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              if (value === 'OK') {
                return (
                  <Chip
                    label="success"
                    sx={{ borderColor: '#4caf50', color: '#4caf50' }}
                    variant="outlined"
                    icon={<DoneIcon />}
                  />
                )
              }
              if (value === 'KO') {
                return (
                  <Chip
                    label="Error"
                    sx={{ borderColor: '#d32f2f', color: '#d32f2f' }}
                    variant="outlined"
                    icon={<ErrorOutline />}
                  />
                )
              }
              if (value === 'RUNNING') {
                return (
                  <Chip
                    label="Running"
                    sx={{ borderColor: '#0288d1', color: '#0288d1' }}
                    variant="outlined"
                    icon={<DirectionsRunIcon />}
                  />
                )
              }
              return <Chip label="N/A" color="default" variant="outlined" />
            },
          }
        }
        if (column.field === 'params') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              if (Array.isArray(value) && value.length > 0) {
                return value.map((arg, index) => (
                  <div key={index}>
                    <b>{arg.key}</b> : {arg.value}
                  </div>
                ))
              }
              return null
            },
          }
        }
        if (column.field === 'start_time') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return value ? new Date(value + 'Z').toLocaleString() : '--'
            },
          }
        }
        if (column.field === 'end_time') {
          return {
            ...baseColumn,
            Cell: ({ row }) => {
              const endTime = row.original.end_time
              const startTime = row.original.start_time
              if (!endTime || !startTime) return 'N/A'

              const seconds = (new Date(endTime) - new Date(startTime)) / 1000
              if (seconds < 0) return 'N/A'

              if (seconds < 3600) return new Date(seconds * 1000).toISOString().slice(14, 19)
              return new Date(seconds * 1000).toISOString().slice(11, 19)
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => <span>{cell.getValue() ?? ''}</span>,
        }
      }),
    [formatMessage]
  )

  const processedData = useMemo(() => {
    return data.map((row) => ({
      ...row,
      job_timing: row.job_timing ?? 'N/A',
      last_report: 'success', // This seems to be hardcoded, keeping it
    }))
  }, [data])

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      draggable: false,
      tableLayout: 'fixed',
      sorting: false,
    }),
    []
  )

  const handleRowClick = useCallback(
    (_, row) => {
      onClickReportTableRow(row.id)
    },
    [onClickReportTableRow]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
    />
  )
}
